/* eslint-disable no-case-declarations */
import { Injectable, effect, inject, signal, untracked } from '@angular/core'
import { Router } from '@angular/router'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { routerCancelAction, routerNavigatedAction } from '@ngrx/router-store'
import { Store } from '@ngrx/store'
import { every as _every } from 'lodash'
import { v4 as uuid } from 'uuid'

import {
  EMPTY,
  catchError,
  delay,
  filter,
  lastValueFrom,
  map,
  mergeMap,
  of,
  pairwise,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
  withLatestFrom,
} from 'rxjs'

import { FlxModalService } from '@flowx/angular-ui-toolkit'
import {
  AiAnalyticsAction,
  AiAnalyticsAgentRequestEvent,
  AiAnalyticsAgentRequestParams,
  AiAnalyticsAgentResponseEvent,
  AiAnalyticsAgentResponseParams,
  AiAnalyticsObject,
  AiAnalyticsSessionFeedbackEvent,
  AiAnalyticsSessionFeedbackParams,
  AiAnalyticsSessionFinishedEvent,
  AiAnalyticsSessionFinishedParams,
  AiAnalyticsSessionStartedEvent,
  AiAnalyticsSessionStartedParams,
} from '@flowx/designer/modules/analytics/analytics-ai.entity'
import { AnalyticsService } from '@flowx/designer/modules/analytics/analytics.service'
import { MediaLibraryImage } from '@flowx/designer/modules/core/dictionary/media-library.dictionary'
import { SystemApiService } from '@flowx/designer/modules/integration-designer/services/system.api'
import { AiAnalystAPI } from '@flowx/designer/modules/new-ai/agents/analyst/api/analyst.api'
import { AiArchitectAPI } from '@flowx/designer/modules/new-ai/agents/architect/api/architect.api'
import { AiAuditorApi } from '@flowx/designer/modules/new-ai/agents/auditor/api/auditor.api'
import { AiDesignerAPI } from '@flowx/designer/modules/new-ai/agents/designer/api/designer.api'
import { AiDeveloperAPI } from '@flowx/designer/modules/new-ai/agents/developer/api/developer.api'
import {
  AiDocumentsApi,
  DocumentUploadResponse,
} from '@flowx/designer/modules/new-ai/agents/documents/api/documents.api'
import { AiDocumentsTransformService } from '@flowx/designer/modules/new-ai/agents/documents/services/ai-documents-transform.service'
import {
  AiInspectorApi,
  WarningCategory,
} from '@flowx/designer/modules/new-ai/agents/inspector/api/inspector.api'
import { AiIntegratorApi } from '@flowx/designer/modules/new-ai/agents/integrator/api/integrator.api'
import {
  AiAgent,
  FeedbackType,
  FileExtension,
  InitialMessageContext,
  SendFeedbackPayload,
  UploadContextFile,
  documentFileTypes,
  processFileTypes,
} from '@flowx/designer/modules/new-ai/common/ai.dictionary'
import { AiFeedbackModalComponent } from '@flowx/designer/modules/new-ai/modules/chat/components/ai-feedback-modal/ai-feedback-modal.component'
// eslint-disable-next-line import/no-cycle
import { CommandPaletteService } from '@flowx/designer/modules/new-ai/modules/command-palette/components/command-palette/command-palette.service'
import { AiContextService } from '@flowx/designer/modules/new-ai/store/context.service'
import { BpmnEditorStores } from '@flowx/designer/shared/effector/bpmn-editor/bpmn-editor.stores'
import { MediaLibraryService } from '@flowx/designer/shared/services/media-library.service'
import { UrlParametersStoreService } from '@flowx/designer/shared/services/url-parameters-store.service'
import { selectDataModelEntitiesArray } from '@flowx/designer/shared/store/selectors/data-model.selectors'
import { selectAllDataModelEntities } from '@flowx/designer/shared/store/selectors/data-model.selectors'
import {
  selectProcessResourceId,
  selectWorkspaceId,
} from '@flowx/designer/shared/store/selectors/router.selectors'
import { selectUiFlowResourceId } from '@flowx/designer/shared/store/selectors/template-config.selectors'

import { ProjectActionsService } from './actions.utils'
import { AiActions, CommandPaletteActions, CtaActions, NodeMenuActions } from './ai.actions'
// eslint-disable-next-line import/no-cycle
import { AiSelectors } from './ai.selectors'

import { BpmnEditorEvents } from '../../bpmn-editor/store/bpmn-editor.events'
import { ActionHandler } from '../../core/dictionary/process.dictionary'
import { ActionTreeEvents } from '../../process/components/actions-tree/actions-tree.service'
import { NodeConfigTabs } from '../../process/components/node-details/node-details.types'
import { AiAnalystUtils } from '../agents/analyst/services/analyst.service'
import { AiAnalystActions } from '../agents/analyst/store/analyst.actions'
import { AiArchitectActions } from '../agents/architect/store/architect.actions'
import { AiAssistantActions } from '../agents/assistant/store/assistant.actions'
import { AiAuditorActions } from '../agents/auditor/store/auditor.actions'
import { AiCoordinatorActions } from '../agents/coordinator/store/coordinator.actions'
import { AiDesignerActions } from '../agents/designer/store/designer.actions'
import { AiDeveloperActions } from '../agents/developer/store/developer.actions'
import { AiDocumentsActions } from '../agents/documents/store/documents.actions'
import { AiInspectorActions } from '../agents/inspector/api/store/inspector.actions'
import { AiIntegratorActions } from '../agents/integrator/store/integrator.actions'
import { showAiModal } from '../common/services/ai-modal.service'

@Injectable()
/* eslint-disable @typescript-eslint/member-ordering */
export class AiEffects {
  #actions$ = inject(Actions)
  #store = inject(Store)
  #aiDocumentsApi = inject(AiDocumentsApi)
  #mediaLibraryService = inject(MediaLibraryService)
  #modalService = inject(FlxModalService)
  #urlParamsStore = inject(UrlParametersStoreService)
  #analyticsService = inject(AnalyticsService)
  #aiArchitectApiService = inject(AiArchitectAPI)
  #aiInspectorApiService = inject(AiInspectorApi)
  #aiIntegratorApiService = inject(AiIntegratorApi)
  #systemApiService = inject(SystemApiService)
  #aiContextService = inject(AiContextService)
  #aiDesignerApiService = inject(AiDesignerAPI)
  #aiDeveloperApiService = inject(AiDeveloperAPI)
  #aiAnalystApiService = inject(AiAnalystAPI)
  #aiAuditorApiService = inject(AiAuditorApi)
  #commandPaletteService = inject(CommandPaletteService)
  #projectActionsService = inject(ProjectActionsService)
  #router = inject(Router)
  #workspaceId = this.#store.selectSignal(selectWorkspaceId)
  #processResourceId = this.#store.selectSignal(selectProcessResourceId)

  #projectBasePath = (appId: string, appVersion: string): string[] => [
    'guard',
    'workspace',
    this.#workspaceId(),
    'projects',
    appId,
    'config',
    appVersion,
  ]

  #hasPrevRequest = false
  #previousAnalyticsSessionData = signal({ agent: null, session_id: null, session_length: 0 })

  onTypeAreaSubmit = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.typeAreaSubmit),
      map(({ text, files, agent }) => {
        if (files?.length && files?.length > 0) {
          return AiActions.generateFromFiles({ files, prompt: text, agent })
        }
        return AiActions.generate({ prompt: text, agent })
      })
    )
  )

  onSelectInterruptOption = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.selectInterruptOption),
      map(({ option }) => AiActions.generate({ prompt: option.value }))
    )
  )

  onIntroMessage = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.introMessage),
      map(() =>
        AiCoordinatorActions.getIntro({
          action_type: 'user_interface',
          message: null,
          selectedAgent: AiAgent.DESIGNER,
        })
      )
    )
  )

  onRouteToAgent = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.routeToAgent),
      withLatestFrom(
        this.#store.select(AiSelectors.pendingRouteToAssistant),
        this.#store.select(AiSelectors.pendingRouteToDesigner),
        this.#store.select(AiSelectors.pendingRouteToAnalyst),
        this.#store.select(AiSelectors.pendingRouteToDeveloper),
        this.#store.select(AiSelectors.pendingRouteToCoordinator),
        this.#store.select(AiSelectors.pendingRouteToArchitect),
        this.#store.select(AiSelectors.pendingRouteToIntegrator),
        this.#store.select(AiSelectors.pendingRouteToAuditor),
        this.#store.select(AiSelectors.selectedAgent),
        this.#store.select(AiSelectors.selectedPlan),
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.waitingForHumanApproval),
        this.#store.select(AiSelectors.pageRouteParams),
        this.#store.select(AiSelectors.selectedDocument),
        this.#store.select(AiSelectors.pageLocation)
      ),
      switchMap(
        ([
          ,
          pendingRouteToAssistant,
          pendingRouteToDesigner,
          pendingRouteToAnalyst,
          pendingRouteToDeveloper,
          pendingRouteToCoordinator,
          pendingRouteToArchitect,
          pendingRouteToIntegrator,
          pendingRouteToAuditor,
          selectedAgent,
          selectedPlan,
          session_id,
          waitingForHumanApproval,
          pageRouteParams,
          selectedDocument,
          routePageLocation,
        ]) => {
          // Clear pending routes immediately after reading them to prevent double dispatch
          this.#store.dispatch(AiCoordinatorActions.routeToAgentConsumed())

          switch (selectedAgent) {
            case AiAgent.ASSISTANT:
              return of(
                AiAssistantActions.getAnswer({
                  prompt: pendingRouteToAssistant.prompt,
                  appId: pendingRouteToAssistant.appId,
                  appVersion: pendingRouteToAssistant.appVersion,
                  processDoc: false,
                })
              )
            case AiAgent.DESIGNER: {
              // Check if we are on a UI flow page — if so, use the UI flow
              // generation pipeline instead of the process UI pipeline.
              const currentPageLocation = this.#store.selectSignal(AiSelectors.pageLocation)()
              const isUiFlowPage = currentPageLocation?.includes('UI_FLOWS')

              if (isUiFlowPage) {
                // Already on the UI flow designer page — no navigation needed.
                // Dispatch the UI flow generation stream.
                return of(
                  AiDesignerActions.generateUiFlowStream({
                    prompt: pendingRouteToDesigner.prompt,
                  })
                )
              }

              const designerProcessId =
                pendingRouteToDesigner.processId ??
                pageRouteParams.processId ??
                pageRouteParams['processId']
              const designerAreaId =
                pendingRouteToDesigner.areaId ?? pageRouteParams.areaId ?? pageRouteParams['areaId']
              if (designerAreaId != null && designerProcessId != null) {
                const route = [
                  ...this.#projectBasePath(
                    pendingRouteToDesigner.appId,
                    pendingRouteToDesigner.appVersion
                  ),
                  'processes',
                  designerProcessId,
                  designerAreaId,
                  'ui',
                ]
                this.#router.navigate(route, {
                  queryParamsHandling: 'merge',
                  queryParams: {
                    nodeId: String(pendingRouteToDesigner.nodeId),
                    boundaryEventId: null,
                    swimlaneId: String(pendingRouteToDesigner.swimlaneId),
                    pivotAreaId: null,
                    templateIds: null,
                  },
                })
              }
              return of(
                AiDesignerActions.generateUiStream({
                  prompt: 'Create a new UI: ' + pendingRouteToDesigner.prompt,
                })
              )
            }
            case AiAgent.ANALYST:
              if (!pendingRouteToAnalyst.processId) {
                lastValueFrom(
                  this.#projectActionsService.create_process_prompt(
                    pendingRouteToAnalyst.processCode,
                    pendingRouteToAnalyst.appId,
                    pendingRouteToAnalyst.appVersion
                  )
                ).then((response) => {
                  // update current plan step destination id with response.id
                  try {
                    const currentPlanStep = this.#store.selectSignal(AiSelectors.currentPlanStep)()
                    if (currentPlanStep && response?.id) {
                      const newStep = {
                        step_number: currentPlanStep.step_number,
                        step_destination: {
                          ...(currentPlanStep.step_destination || {}),
                          id: response.id,
                        },
                      }
                      this.#store.dispatch(
                        AiActions.updateStep({ updateStep: newStep, agent: AiAgent.ANALYST })
                      )
                    }
                  } catch (e) {}
                })
              } else {
                this.#router.navigate([
                  ...this.#projectBasePath(
                    pendingRouteToAnalyst.appId,
                    pendingRouteToAnalyst.appVersion
                  ),
                  'processes',
                  pendingRouteToAnalyst.processId,
                  'diagram',
                ])
              }
              return of(
                AiAnalystActions.generateProcessStream({
                  prompt: 'Create a new business process: ' + pendingRouteToAnalyst.prompt,
                })
              )
            case AiAgent.DEVELOPER: {
              // On custom component pages, skip process navigation — already on the right page
              if (routePageLocation?.includes('CUSTOM_COMPONENTS')) {
                return of(
                  AiDeveloperActions.generateCustomComponent({
                    prompt: pendingRouteToDeveloper?.prompt || '',
                  })
                )
              }
              // On reusable function pages, skip process navigation — already on the right page
              if (routePageLocation?.includes('REUSABLE_FUNCTIONS')) {
                return of(
                  AiDeveloperActions.generateReusableFunction({
                    prompt: pendingRouteToDeveloper?.prompt || '',
                  })
                )
              }
              // open panel and jump to actions tab
              const developerProcessId =
                pendingRouteToDeveloper.processId ??
                pageRouteParams.processId ??
                pageRouteParams['processId']
              this.#router.navigate(
                [
                  ...this.#projectBasePath(
                    pendingRouteToDeveloper.appId,
                    pendingRouteToDeveloper.appVersion
                  ),
                  'processes',
                  developerProcessId,
                  'diagram',
                ],
                {
                  queryParamsHandling: 'merge',
                  queryParams: {
                    nodeId: String(pendingRouteToDeveloper.nodeId),
                    nodePanelOpened: 'true',
                  },
                }
              )

              BpmnEditorEvents.nodeActionOpenConfigPanel()

              if (pendingRouteToDeveloper.action_type === 'gateway_condition') {
                setTimeout(() => {
                  BpmnEditorEvents.nodeActionChangeConfigPanelTab(NodeConfigTabs.RULES)
                }, 500)
                return of(
                  AiDeveloperActions.generateGatewayCondition({
                    prompt: pendingRouteToDeveloper.prompt,
                    nodeId: pendingRouteToDeveloper.nodeId,
                  })
                )
              }

              setTimeout(() => {
                BpmnEditorEvents.nodeActionChangeConfigPanelTab(NodeConfigTabs.ACTIONS)
                if (pendingRouteToDeveloper.action_type === 'business_rule') {
                  const currentNode = BpmnEditorStores.selectedNodeDenormalized$.getState()
                  const currentNodeActions =
                    currentNode?.actionDefinitions?.filter((action) => !action.parentFlowxUuid) ??
                    []
                  ActionTreeEvents.addActionClick({
                    node: BpmnEditorStores.selectedNode$.getState(),
                    parentAction: null,
                    siblings: currentNodeActions,
                    handlerService: ActionHandler.NO_OP,
                  })

                  setTimeout(() => {
                    BpmnEditorEvents.actionLoadingSuccess({ withAi: true })
                  }, 500)
                }
              }, 500)
              return EMPTY
            }
            case AiAgent.ARCHITECT: {
              // On project data model page, skip navigation — already on the right page
              if (routePageLocation?.includes('PROJECT_DATA_MODEL')) {
                return of(
                  AiArchitectActions.generateDataModelStream({
                    prompt: pendingRouteToArchitect?.prompt || '',
                  })
                )
              }
              const architectProcessId =
                pendingRouteToArchitect?.processId || this.#processResourceId()
              if (!architectProcessId) {
                return EMPTY
              }
              this.#router.navigate([
                ...this.#projectBasePath(
                  pendingRouteToArchitect.appId,
                  pendingRouteToArchitect.appVersion
                ),
                'processes',
                architectProcessId,
                'data-model',
              ])
              return of(
                AiArchitectActions.generateDataModelStream({
                  prompt: 'Create a data model: ' + pendingRouteToArchitect.prompt,
                })
              )
            }
            case AiAgent.AUDITOR: {
              // Detect UI vs Process surface so we hit the right action.
              // SSE-mode actions stream from /auditor/chat — chunks land in
              // the chat panel as they arrive, then streamCompleted fires.
              const auditPrompt = pendingRouteToAuditor?.prompt || ''
              const auditLocation = routePageLocation || ''
              const isUiSurface =
                auditLocation.includes('USER_INTERFACE') ||
                auditLocation.includes('UI_FLOWS') ||
                auditLocation.includes('REUSABLE_UI_TEMPLATES') ||
                auditLocation.includes('UI_DESIGNER')
              if (isUiSurface) {
                return of(AiAuditorActions.checkUIStream({ prompt: auditPrompt }))
              }
              return of(AiAuditorActions.checkProcessStream({ prompt: auditPrompt }))
            }
            case AiAgent.COORDINATOR:
              const contextAnalyst = AiAnalystUtils.getInitialProcessContext()
              const totalNodes = contextAnalyst
                ? contextAnalyst.sl?.reduce((sum, section) => sum + (section.nl?.length || 0), 0)
                : 0
              return of(
                AiCoordinatorActions.generatePlan({
                  prompt: pendingRouteToCoordinator.prompt,
                  appId: pendingRouteToCoordinator.appId,
                  appVersion: pendingRouteToCoordinator.appVersion,
                  plan_type: pendingRouteToCoordinator.plan_type,
                  current_plan: selectedPlan,
                  context: totalNodes > 0 ? contextAnalyst : null,
                  session_id,
                  human_feedback: waitingForHumanApproval ? pendingRouteToCoordinator.prompt : null,
                  document_id: selectedDocument?.documentId ?? null,
                })
              )
            case AiAgent.INTEGRATOR: {
              const intLocation = this.#store.selectSignal(AiSelectors.pageLocation)()
              const isOnDetailPage = intLocation?.includes('DATA_SOURCE_DETAILS')

              if (isOnDetailPage) {
                // Edit mode — fetch system from route params, then dispatch edit
                const routeParams = this.#store.selectSignal(AiSelectors.pageRouteParams)()
                const resourceId = routeParams?.['resourceId']
                if (resourceId) {
                  return this.#systemApiService.getSystemById(resourceId).pipe(
                    switchMap((systemResponse: any) => {
                      const system = systemResponse?.resourceContent || systemResponse
                      this.#store.dispatch(
                        AiActions.setCurrentSystemContext({ currentSystem: system })
                      )
                      return of(
                        AiIntegratorActions.editDatasource({
                          prompt: pendingRouteToIntegrator.prompt,
                          currentSystem: system,
                        })
                      )
                    }),
                    catchError(() =>
                      of(
                        AiActions.getAnswerFailure({
                          statusCode: 500,
                          errorMsg: 'Failed to load system',
                        })
                      )
                    )
                  )
                }
              }

              // Create mode — navigate to data sources list
              this.#router.navigate([
                ...this.#projectBasePath(
                  pendingRouteToIntegrator.appId,
                  pendingRouteToIntegrator.appVersion
                ),
                'integration-designer',
                'data-sources',
              ])
              return of(
                AiIntegratorActions.generateDatasource({
                  prompt: pendingRouteToIntegrator.prompt,
                  openapiUrl: pendingRouteToIntegrator.openapi_url,
                })
              )
            }
          }
        }
      )
    )
  )

  onAskAssistantCommand = createEffect(() =>
    this.#actions$.pipe(
      ofType(CommandPaletteActions.askAssistantCommand),
      map(({ question, processDoc }) =>
        AiAssistantActions.getAnswer({
          prompt: question,
          appId: this.#commandPaletteService.getApplicationId(),
          appVersion: this.#commandPaletteService.getApplicationVersionId(),
          processDoc,
        })
      )
    )
  )
  // TODO: move remaining logic from file-context-service
  onFileUploaded = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiActions.fileUploaded),
        map(({ event }) => this.#mapEventFilesToUploadContextFile(event)),
        filter((files) => !!files?.length),
        withLatestFrom(this.#store.select(AiSelectors.agentFileUploadConfig)),
        filter(([files, fileUploadConfig]) =>
          this.#areFilesAllowed(files, fileUploadConfig?.fileType)
        ),
        map(([files]) =>
          this.#areAllFilesOfTypeDocument(files)
            ? AiDocumentsActions.uploadDocuments({ documents: files })
            : this.#areAllFilesOfTypeProcess(files)
              ? AiAnalystActions.convertBPMN({ processFiles: [files[0].file] })
              : AiActions.fileUploadSuccess({ files })
        )
      ),
    { dispatch: true }
  )

  onFromDocumentsClick = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.fromDocumentsClick),
      map(() => AiDocumentsActions.loadDocuments())
    )
  )

  onGenerateFromDocumentClick = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.generateFromDocumentClick),
      withLatestFrom(
        this.#store.select(AiSelectors.selectedAgent),
        this.#store.select(AiSelectors.pageAgent),
        this.#store.select(AiSelectors.currentUI),
        this.#store.select(AiSelectors.sessionId)
      ),

      map(([{ documentContextReference }, , pageAgent, currentUI, session_id]) => {
        switch (pageAgent) {
          case AiAgent.ANALYST:
            documentContextReference = {
              ...documentContextReference,
              session_id,
            }
            return AiAnalystActions.extractProcessFromDocument({ documentContextReference })
          case AiAgent.DESIGNER:
            documentContextReference = {
              ...documentContextReference,
              session_id,
            }
            return AiDesignerActions.extractUiFromDocument({ documentContextReference })
          case AiAgent.DEVELOPER:
            documentContextReference = {
              ...documentContextReference,
              session_id,
            }
            return AiDeveloperActions.extractBusinessRuleFromDocument({
              documentContextReference,
            })
          case AiAgent.AUDITOR:
            if (pageAgent === AiAgent.ANALYST) {
              documentContextReference = {
                ...documentContextReference,
                session_id,
                type: 'compliance_check_process',
              }
              return AiAuditorActions.extractComplianceFromDocument({
                documentContextReference,
                context: AiAnalystUtils.getInitialProcessContext(),
              })
            } else if (pageAgent === AiAgent.DESIGNER) {
              documentContextReference = {
                ...documentContextReference,
                session_id,
                type: 'compliance_check_ui',
              }
              return AiAuditorActions.extractComplianceFromDocument({
                documentContextReference,
                context: currentUI,
              })
            }
        }
      })
    )
  )

  loadDocuments = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDocumentsActions.loadDocuments),
      withLatestFrom(
        this.#store.select(AiSelectors.selectedAgent),
        this.#store.select(AiSelectors.pageAgent)
      ),
      switchMap(([, selectedAgent]) =>
        this.#aiDocumentsApi.getDocuments(selectedAgent).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((documents) => {
            const filtered_docs =
              AiDocumentsTransformService.contextDocumentResponseTransform(documents)
            return AiDocumentsActions.loadDocumentsSuccess({
              documents: filtered_docs,
            })
          })
        )
      )
    )
  )

  uploadDocument = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDocumentsActions.uploadDocuments),
      map(({ documents }) =>
        AiDocumentsTransformService.mapDocumentsToMediaLibraryUploadPayload(documents[0])
      ),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.pageAgent)
      ),
      switchMap(([payload, session_id, pageAgent]) =>
        this.#mediaLibraryService.addImage(payload, { suppressToasts: true }).pipe(
          switchMap((document: MediaLibraryImage & { contentType: string }) => {
            const { appId, appVersionId } = this.#urlParamsStore.urlParameters

            const ssePayload = {
              unique_id: payload.key,
              object_name: document.storagePath,
              filename: document.filename,
              content_type: document.contentType,
              application_id: appId,
              application_version_id: appVersionId,
              workspace_id: this.#workspaceId(),
              resource_id: document.id,
              wait_for_summary: true,
              new_model: true,
              session_id,
            }

            let sseBuffer = ''
            return this.#aiDocumentsApi.extractFromDocumentSSE(ssePayload).pipe(
              mergeMap((chunk: string) => {
                sseBuffer += chunk.replace(/\r\n/g, '\n')
                const parts = sseBuffer.split('\n\n')
                sseBuffer = parts.pop() || ''
                return this.#parseSSEEvents(parts)
              }),
              tap((event: any) => {
                if (event.type === 'status') {
                  this.#store.dispatch(
                    AiDocumentsActions.uploadDocumentSSEStatus({
                      message: event.message,
                      subtitle: event.subtitle,
                    })
                  )
                }
              }),
              filter((event: any) => event.type === 'complete' || event.type === 'error'),
              mergeMap((event: any) => {
                if (event.type === 'error') {
                  return throwError(() => ({ status: 500, error: event.message }))
                }
                const uploadResponse: DocumentUploadResponse = {
                  files_status: [
                    {
                      unique_id: event.doc_id || event.unique_id,
                      fileName: document.filename,
                      response: event.response,
                      status: event.status,
                    },
                  ],
                }
                return of(
                  AiDocumentsActions.uploadDocumentsSuccess({
                    documents:
                      AiDocumentsTransformService.uploadDocumentResponseTransform(uploadResponse),
                    pageAgent,
                  })
                )
              })
            )
          }),
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          catchError(({ status, error }) =>
            of(AiDocumentsActions.uploadDocumentsFailure({ statusCode: status, errorMsg: error }))
          )
        )
      )
    )
  )

  generateFromDocument = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.generateFromDocument),
      withLatestFrom(
        this.#store.select(AiSelectors.selectedAgent),
        this.#store.select(AiSelectors.selectedDocumentContext),
        this.#store.select(AiSelectors.sessionId)
      ),
      filter(([, selectedAgent]) =>
        [AiAgent.ASSISTANT, AiAgent.ANALYST, AiAgent.DESIGNER].includes(selectedAgent)
      ),
      map(([, selectedAgent, documentContextReference, session_id]) => {
        switch (selectedAgent) {
          case AiAgent.ANALYST:
            documentContextReference = {
              ...documentContextReference,
              session_id,
            }
            return AiAnalystActions.extractProcessFromDocument({ documentContextReference })
          case AiAgent.DESIGNER:
            documentContextReference = {
              ...documentContextReference,
              session_id,
            }
            return AiDesignerActions.extractUiFromDocument({ documentContextReference })
        }
      })
    )
  )

  generateFromFiles = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.generateFromFiles),
      withLatestFrom(
        this.#store.select(AiSelectors.pageAgent),
        this.#store.select(AiSelectors.selectedAgent),
        this.#store.select(AiSelectors.isZenMode)
      ),
      map(([payload, pageAgent, selectedAgent, isZenMode]) => {
        const { files, prompt } = payload
        const hasBpmnFiles = files.some((file) => file.name.endsWith(FileExtension.BPMN))

        if (pageAgent === AiAgent.DESIGNER) {
          const currentPageLocation = this.#store.selectSignal(AiSelectors.pageLocation)()
          if (currentPageLocation?.includes('REUSABLE_UI_TEMPLATES')) {
            return AiDesignerActions.generateUiTemplateFromImagesStream({
              imageFiles: files,
              prompt,
            })
          }
          return AiDesignerActions.generateUiFromImagesStream({ imageFiles: files, prompt })
        }
        if (pageAgent === AiAgent.ANALYST) {
          if (isZenMode) {
            return AiAnalystActions.generateProcessFromImagesStream({ files, prompt })
          } else {
            return hasBpmnFiles
              ? AiAnalystActions.generateProcessFromBpmnFiles({ files, prompt })
              : AiAnalystActions.generateProcessFromImages({
                  files,
                  prompt,
                })
          }
        }
        switch (selectedAgent) {
          case AiAgent.ANALYST:
            return hasBpmnFiles
              ? AiAnalystActions.generateProcessFromBpmnFiles({ files, prompt })
              : AiAnalystActions.generateProcessFromImages({
                  files,
                  prompt,
                })
          case AiAgent.DESIGNER: {
            const templateLocation = this.#store.selectSignal(AiSelectors.pageLocation)()
            if (templateLocation?.includes('REUSABLE_UI_TEMPLATES')) {
              return AiDesignerActions.generateUiTemplateFromImagesStream({
                imageFiles: files,
                prompt,
              })
            }
            return AiDesignerActions.generateUiFromImagesStream({ imageFiles: files, prompt })
          }
        }
      })
    )
  )

  generate = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiActions.generate),
        withLatestFrom(
          this.#store.select(AiSelectors.selectedAgent),
          this.#store.select(AiSelectors.isZenMode),
          this.#store.select(AiSelectors.pageAgent),
          this.#store.select(AiSelectors.pageAgentActions),
          this.#store.select(AiSelectors.pageLocation),
          this.#store.select(AiSelectors.sessionId),
          this.#store.select(AiSelectors.waitingForHumanApproval),
          this.#store.select(AiSelectors.generatedIntent),
          this.#store.select(AiSelectors.autonomyLevel)
        ),
        // TODO: Check why the payloads are different (prompt vs content)
        map(
          ([
            { prompt, agent },
            selectedAgent,
            zenMode,
            ,
            agentActions,
            pageLocation,
            session_id,
            waitingForHumanApproval,
            generatedIntent,
            autonomyLevel,
          ]) => {
            // Direct dispatch for coordinator plan generation (e.g. from "Generate Plan" button)
            if (agent === AiAgent.COORDINATOR) {
              return AiCoordinatorActions.generatePlan({
                prompt,
                appId: this.#commandPaletteService.getApplicationId(),
                appVersion: this.#commandPaletteService.getApplicationVersionId(),
                plan_type: 'application',
                current_plan: null,
                context: null,
                session_id,
                document_id:
                  this.#store.selectSignal(AiSelectors.selectedDocument)()?.documentId ?? null,
              })
            }

            // Override agent selection based on page context
            if (pageLocation?.includes('CUSTOM_COMPONENTS')) {
              return AiDeveloperActions.generateCustomComponent({ prompt })
            }

            if (!zenMode) {
              const currentAgentCheck = agent || selectedAgent
              switch (currentAgentCheck) {
                case AiAgent.ASSISTANT:
                  return AiAssistantActions.getAnswer({
                    prompt,
                    appId: this.#commandPaletteService.getApplicationId(),
                    appVersion: this.#commandPaletteService.getApplicationVersionId(),
                    processDoc: false,
                  })
                case AiAgent.ANALYST:
                  if (zenMode) {
                    return AiAnalystActions.generateProcessStream({ prompt })
                  } else {
                    return AiAnalystActions.generateProcess({ prompt })
                  }
                case AiAgent.DESIGNER: {
                  // On UI flow pages, use the UI flow generation pipeline
                  const isOnUiFlowPage = pageLocation?.includes('UI_FLOWS')
                  if (isOnUiFlowPage) {
                    return AiDesignerActions.generateUiFlowStream({ prompt })
                  }
                  // On reusable UI template pages, use the template generation pipeline
                  const isOnUiTemplatePage = pageLocation?.includes('REUSABLE_UI_TEMPLATES')
                  if (isOnUiTemplatePage) {
                    return AiDesignerActions.generateUiTemplateStream({ prompt })
                  }
                  return AiDesignerActions.generateUiStream({ prompt })
                }
                case AiAgent.DEVELOPER: {
                  const isOnReusableFunctionPage = pageLocation?.includes('REUSABLE_FUNCTIONS')
                  if (isOnReusableFunctionPage) {
                    return AiDeveloperActions.generateReusableFunction({ prompt })
                  }
                  return AiDeveloperActions.generate({ prompt })
                }
                case AiAgent.ARCHITECT:
                  switch (agentActions) {
                    case 'enumerations':
                      return AiArchitectActions.generateEnumeration({ prompt })
                    case 'document-templates':
                      return AiArchitectActions.generateDocTemplate({ prompt })
                    case 'notification-templates':
                      return AiArchitectActions.generateNotTemplate({ prompt })
                    case 'data-model':
                      return AiArchitectActions.generateDataModel({ prompt })
                    case 'substitution-tags':
                      return AiArchitectActions.translateSubstitutionTags({ prompt })
                    case 'image':
                      return AiArchitectActions.generateImage({ prompt })
                    default:
                  }
              }
            } else {
              return AiCoordinatorActions.getIntent({
                prompt,
                location: pageLocation,
                appId: this.#commandPaletteService.getApplicationId(),
                appVersion: this.#commandPaletteService.getApplicationVersionId(),
                session_id,
                generatedIntent: waitingForHumanApproval ? generatedIntent : null,
                autonomy_level:
                  autonomyLevel === 0 ? 'low' : autonomyLevel === 1 ? 'medium' : 'high',
                recommended_intent: agent ? 'route_to_agent: ' + agent : null,
              })
            }
          }
        )
      ),
    { dispatch: true }
  )

  onCloseContextDocumentConfirmed = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.closeContextDocumentConfirmed),
      withLatestFrom(this.#store.select(AiSelectors.hasDraft)),
      filter(([, hasDraft]) => hasDraft),
      map(() => AiActions.discardDraft())
    )
  )

  onKeepDraftClick = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.keepDraftClick),
      withLatestFrom(
        this.#store.select(AiSelectors.selectedAgent),
        this.#store.select(AiSelectors.pageAgent),
        this.#store.select(AiSelectors.pendingAiResponse),
        this.#store.select(AiSelectors.pendingKeepDraftSave),
        this.#store.select(AiSelectors.isZenMode),
        this.#store.select(AiSelectors.currentPlanStep)
      ),
      filter(
        ([, selectedAgent, pageAgent, pendingAiResponse, pendingKeepDraftSave, isZenMode]) =>
          !pendingAiResponse &&
          !pendingKeepDraftSave &&
          [
            AiAgent.ANALYST,
            AiAgent.DESIGNER,
            AiAgent.DEVELOPER,
            AiAgent.AUDITOR,
            AiAgent.ARCHITECT,
            AiAgent.INTEGRATOR,
          ].includes(isZenMode ? (pageAgent ?? selectedAgent) : selectedAgent)
      ),
      map(([, selectedAgent, pageAgent, , , isZenMode, currentPlanStep]) => {
        const planStepAgent =
          isZenMode && currentPlanStep?.parentAgent ? currentPlanStep.parentAgent : null
        const currentAgent = planStepAgent || pageAgent || selectedAgent
        switch (currentAgent) {
          case AiAgent.ANALYST:
            return AiAnalystActions.keepProcessDraft()
          case AiAgent.DESIGNER: {
            // When on a UI flow page, dispatch the UI flow keep draft action
            const currentPageLocation = this.#store.selectSignal(AiSelectors.pageLocation)()
            if (currentPageLocation?.includes('UI_FLOWS')) {
              const resourceId = this.#store.selectSignal(selectUiFlowResourceId)()
              if (resourceId) {
                return AiDesignerActions.keepUiFlowDraft({ resourceId })
              }
            }
            return AiDesignerActions.keepUiDraft()
          }
          case AiAgent.DEVELOPER:
            return AiDeveloperActions.keepDraft()
          case AiAgent.ARCHITECT:
            return AiArchitectActions.keepDraft()
          case AiAgent.AUDITOR:
            return pageAgent === AiAgent.ANALYST
              ? AiAnalystActions.keepProcessDraft()
              : AiDesignerActions.keepUiDraft()
          case AiAgent.INTEGRATOR: {
            const intKeepLocation = this.#store.selectSignal(AiSelectors.pageLocation)()
            const hasSystemCtx = this.#store.selectSignal(AiSelectors.currentSystemContext)()
            if (intKeepLocation?.includes('DATA_SOURCE_DETAILS') && hasSystemCtx) {
              return AiIntegratorActions.keepEditedDatasourceDraft()
            }
            return AiIntegratorActions.keepDatasourceDraft()
          }
        }
      })
    )
  )

  onApplyClick = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.applyClick),
      withLatestFrom(
        this.#store.select(AiSelectors.selectedAgent),
        this.#store.select(AiSelectors.pageAgent),
        this.#store.select(AiSelectors.generatedAiRecommendation)
      ),
      filter(([, selectedAgent]) => [AiAgent.AUDITOR].includes(selectedAgent)),
      map(([, , pageAgent, generatedAiRecommendation]) => {
        this.#store.dispatch(AiAuditorActions.discardAuditDraft())
        switch (pageAgent) {
          case AiAgent.ANALYST:
            return AiAnalystActions.generateProcess({ prompt: generatedAiRecommendation })
          case AiAgent.DESIGNER:
            return AiDesignerActions.generateUiStream({ prompt: generatedAiRecommendation })
        }
      })
    )
  )

  onDiscardDraftClick = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.discardDraftClick),
      map(() => AiActions.discardDraft())
    )
  )

  // Fire-and-forget telemetry: Keep / Discard send the acceptance signal
  // to observatory (Run.saved / Run.discarded), the build-side ROI metric
  // for developer agents. Always-on; failure is swallowed so it can't
  // break the draft flow.
  onKeepDraftClickAcceptanceSignal = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiActions.keepDraftClick, AiActions.discardDraftClick),
        withLatestFrom(this.#store.select(AiSelectors.lastRequestId)),
        tap(([action, lastRequestId]) => {
          if (!lastRequestId) {
            return
          }
          const observatoryAction =
            action.type === AiActions.keepDraftClick.type ? 'saved' : 'discarded'
          this.#postObservatoryFeedback(lastRequestId, observatoryAction)
        })
      ),
    { dispatch: false }
  )

  // Same fire-and-forget bridge for thumbs up / down. Existing
  // /ai-engine/agents/<agent>/feedback endpoints only exist for writer, so
  // we don't rely on them — we go straight to observatory.
  // We listen for `sendFeedback`, which fires AFTER the negative modal is
  // confirmed; clicking Cancel never reaches it, so no stray correctness.
  onSendFeedbackCorrectnessSignal = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiActions.sendFeedback),
        withLatestFrom(this.#store.select(AiSelectors.lastRequestId)),
        tap(([{ feedback }, lastRequestId]) => {
          const rid = feedback.requestId ?? lastRequestId
          if (!rid) {
            return
          }
          const observatoryAction =
            feedback.feedbackType === FeedbackType.POSITIVE
              ? 'correctness_good'
              : feedback.feedbackType === FeedbackType.NEGATIVE
                ? 'correctness_bad'
                : 'correctness_unset'
          this.#postObservatoryFeedback(rid, observatoryAction, {
            reason: feedback.formData?.issue,
            message: feedback.formData?.details,
          })
        })
      ),
    { dispatch: false }
  )

  #postObservatoryFeedback(
    requestId: string,
    action: string,
    extra: { reason?: string; message?: string } = {}
  ): void {
    const token = localStorage.getItem('access_token')
    const body: Record<string, unknown> = { request_id: requestId, action }
    if (extra.reason) {
      body['reason'] = extra.reason
    }
    if (extra.message) {
      body['message'] = extra.message
    }
    fetch('ai-engine/agents/observatory/feedback', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    }).catch(() => {})
  }

  discardDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.discardDraft),
      withLatestFrom(
        this.#store.select(AiSelectors.selectedAgent),
        this.#store.select(AiSelectors.pageAgent),
        this.#store.select(AiSelectors.pendingAiResponse),
        this.#store.select(AiSelectors.pendingKeepDraftSave),
        this.#store.select(AiSelectors.isZenMode)
      ),

      map(([, selectedAgent, pageAgent, , ,]) => {
        const currentAgent = selectedAgent
        switch (currentAgent) {
          case AiAgent.ANALYST:
            return AiAnalystActions.discardProcessDraft()
          case AiAgent.DESIGNER: {
            const discardPageLocation = this.#store.selectSignal(AiSelectors.pageLocation)()
            if (discardPageLocation?.includes('UI_FLOWS')) {
              return AiDesignerActions.discardUiFlowDraft()
            }
            return AiDesignerActions.discardUiDraft()
          }
          case AiAgent.DEVELOPER:
            return AiDeveloperActions.discardDraft()
          case AiAgent.AUDITOR:
            this.#store.dispatch(AiAuditorActions.discardAuditDraft())
            return pageAgent === AiAgent.ANALYST
              ? AiAnalystActions.discardProcessDraft()
              : AiDesignerActions.discardUiDraft()
          case AiAgent.ARCHITECT:
            return AiArchitectActions.discardDraft()
          case AiAgent.INTEGRATOR:
            return AiIntegratorActions.discardDatasourceDraft()
          default:
        }
      })
    )
  )

  onFeedbackClick = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.feedbackClick),
      switchMap(({ requestId, feedbackType }) => {
        if (feedbackType === FeedbackType.NEGATIVE) {
          const modal = showAiModal(this.#modalService, AiFeedbackModalComponent, {
            appendTo: 'body',
          })
          return modal.data$.pipe(
            take(1),
            filter(({ confirm }) => !!confirm),
            map(({ reason, feedback }) =>
              AiActions.sendFeedback({
                feedback: {
                  requestId,
                  feedbackType,
                  formData: { issue: reason, details: feedback },
                },
              })
            )
          )
        }
        return of(AiActions.sendFeedback({ feedback: { requestId, feedbackType } }))
      })
    )
  )

  sendFeedback = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.sendFeedback),
      withLatestFrom(this.#store.select(AiSelectors.sessionId)),
      map(
        ([{ feedback }, sessionId]) =>
          ({
            sessionId,
            requestId: feedback.requestId,
            feedbackType: feedback.feedbackType,
            message: feedback.formData?.details,
            reason: feedback.formData?.issue,
          }) satisfies SendFeedbackPayload
      ),
      withLatestFrom(this.#store.select(AiSelectors.selectedAgent)),
      map(([payload, selectedAgent]) => {
        switch (selectedAgent) {
          case AiAgent.ASSISTANT:
            return AiAssistantActions.sendFeedback({ payload })
          case AiAgent.ANALYST:
            return AiAnalystActions.sendFeedback({ payload })
          case AiAgent.DESIGNER:
            return AiDesignerActions.sendFeedback({ payload })
          case AiAgent.DEVELOPER:
            return AiDeveloperActions.sendFeedback({ payload })
        }
      })
    )
  )

  onSelectedAgentReset = createEffect(() =>
    this.#store.select(AiSelectors.selectedAgent).pipe(
      pairwise(),
      withLatestFrom(
        this.#store.select(AiSelectors.pendingAiResponse),
        this.#store.select(AiSelectors.isUploadingDocuments)
      ),
      filter(
        ([[prev, curr], pendingAiResponse, isUploadingDocuments]) =>
          prev !== curr && (pendingAiResponse || isUploadingDocuments)
      ),
      map(() => AiActions.cancelPendingRequest())
    )
  )

  onPageAgentChanged = createEffect(() =>
    this.#actions$.pipe(ofType(routerNavigatedAction, routerCancelAction)).pipe(
      switchMap(() => this.#store.select(AiSelectors.pageAgent).pipe(take(1))),
      pairwise(),
      filter(([prev, curr]) => prev !== curr),
      map(() => AiActions.pageAgentChanged())
    )
  )

  onExplainBusinessRuleCtaClick = createEffect(() =>
    this.#actions$.pipe(
      ofType(CtaActions.explainBusinessRuleCtaClick),
      map(() => AiDeveloperActions.explainBusinessRule())
    )
  )

  onGenerateUiNodeMenuAction = createEffect(() =>
    this.#actions$.pipe(
      ofType(NodeMenuActions.generateUiNodeMenuAction),
      switchMap(({ userTaskNavigationAreaId }) =>
        this.#actions$.pipe(ofType(routerNavigatedAction)).pipe(
          take(1),
          delay(0), // wait for page agent change action
          map(() =>
            NodeMenuActions.generateUiNodeMenuActionTransitionEnd({ userTaskNavigationAreaId })
          )
        )
      )
    )
  )

  onCancelPendingRequestClick = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.cancelPendingRequestClick),
      map(() => AiActions.cancelPendingRequest())
    )
  )

  analyticsAgentEvents = effect(() => {
    const pendingAiResponse = this.#store.selectSignal(AiSelectors.pendingAiResponse)()

    const session_id = this.#store.selectSignal(AiSelectors.sessionId)
    const agent = this.#store.selectSignal(AiSelectors.selectedAgent)

    if (!session_id() && !agent()) {
      return
    }

    if (pendingAiResponse) {
      const document_used = this.#store.selectSignal(AiSelectors.hasSelectedDocument)
      const document_context_used = this.#store.selectSignal(AiSelectors.hasSelectedDocumentContext)
      const document_upload = this.#store.selectSignal(AiSelectors.hasUploadedFiles)

      this.#hasPrevRequest = true
      this.#analyticsService.track(
        {
          object: AiAnalyticsObject.AI,
          action: AiAnalyticsAction.AGENT_REQUEST,
        } satisfies AiAnalyticsAgentRequestEvent,
        {
          agent: agent(),
          session_id: session_id(),
          document_used: document_used(),
          document_context_used: document_context_used(),
          document_upload: document_upload(),
        } satisfies AiAnalyticsAgentRequestParams
      )
    } else if (this.#hasPrevRequest) {
      this.#hasPrevRequest = false
      this.#analyticsService.track(
        {
          object: AiAnalyticsObject.AI,
          action: AiAnalyticsAction.AGENT_RESPONSE,
        } satisfies AiAnalyticsAgentResponseEvent,
        { agent: agent(), session_id: session_id() } satisfies AiAnalyticsAgentResponseParams
      )
    }
  })

  analyticsStartSessionEvents = effect(() => {
    const agent = this.#store.selectSignal(AiSelectors.selectedAgent)()
    const session_id = this.#store.selectSignal(AiSelectors.sessionId)()
    const session_length = this.#store.selectSignal(AiSelectors.messages)().length ?? 0

    const currData = {
      agent,
      session_id,
      session_length,
    }
    let prevData = null
    untracked(() => {
      prevData = this.#previousAnalyticsSessionData()
    })

    if (!prevData.agent && currData.agent) {
      const { agent, session_id } = currData

      this.#analyticsService.track(
        {
          object: AiAnalyticsObject.AI,
          action: AiAnalyticsAction.SESSION_STARTED,
        } satisfies AiAnalyticsSessionStartedEvent,
        { agent, session_id } satisfies AiAnalyticsSessionStartedParams
      )
    } else if (prevData.agent && !currData.agent) {
      const { agent, session_id, session_length } = prevData

      this.#analyticsService.track(
        {
          object: AiAnalyticsObject.AI,
          action: AiAnalyticsAction.SESSION_FINISHED,
        } satisfies AiAnalyticsSessionFinishedEvent,
        {
          agent,
          session_id,
          session_length,
          outcome: 'none',
        } satisfies AiAnalyticsSessionFinishedParams
      )
    }

    this.#previousAnalyticsSessionData.set(currData)
  })

  onKeepProcessDraftSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.keepProcessDraftSuccess),
      withLatestFrom(
        this.#store.select(AiSelectors.selectedPlan),
        this.#store.select(AiSelectors.isZenMode)
      ),
      filter(([, , isZenMode]) => isZenMode),
      switchMap(([, selectedPlan]) => {
        return of(
          AiCoordinatorActions.updatePlan({
            plan_id: selectedPlan.id,
            plan: selectedPlan,
            status: 'in progress',
          })
        )
      })
    )
  )

  onKeepTemplateConfigDraftSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.keepTemplateConfigDraftSuccess),
      withLatestFrom(
        this.#store.select(AiSelectors.selectedPlan),
        this.#store.select(AiSelectors.isZenMode)
      ),
      filter(([, , isZenMode]) => isZenMode),
      switchMap(([, selectedPlan]) => {
        if (!selectedPlan) {
          return of(null)
        }
        return of(
          AiCoordinatorActions.updatePlan({
            plan_id: selectedPlan.id,
            plan: selectedPlan,
            status: 'in progress',
          })
        )
      })
    )
  )

  onKeepAgentDraftSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCoordinatorActions.keepAgentDraftSuccess),
      withLatestFrom(
        this.#store.select(AiSelectors.selectedPlan),
        this.#store.select(AiSelectors.isZenMode)
      ),
      filter(([, , isZenMode]) => isZenMode),
      switchMap(([, selectedPlan]) =>
        of(
          AiCoordinatorActions.updatePlan({
            plan_id: selectedPlan.id,
            plan: selectedPlan,
            status: 'in progress',
          })
        )
      )
    )
  )

  onKeepBusinessRuleDraftSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.keepBusinessRuleDraftSuccess),
      withLatestFrom(
        this.#store.select(AiSelectors.selectedPlan),
        this.#store.select(AiSelectors.isZenMode)
      ),
      filter(([, , isZenMode]) => isZenMode),
      switchMap(([, selectedPlan]) =>
        of(
          AiCoordinatorActions.updatePlan({
            plan_id: selectedPlan.id,
            plan: selectedPlan,
            status: 'in progress',
          }),
          AiActions.forceCloseScriptEditorModal()
        )
      )
    )
  )

  analyticsSessionAcceptedEvents = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(
          AiAnalystActions.keepProcessDraftSuccess,
          AiDesignerActions.keepTemplateConfigDraftSuccess,
          AiDesignerActions.keepDataModelDraftSuccess,
          AiDeveloperActions.keepBusinessRuleDraftSuccess,
          AiDeveloperActions.keepFixWarningsBusinessRuleDraftSuccess,
          AiDeveloperActions.keepExpressionDraftSuccess,
          AiDeveloperActions.keepImprovedCodeDraftSuccess,
          AiDeveloperActions.keepAddCommentDraftSuccess
        ),
        withLatestFrom(
          this.#store.select(AiSelectors.generatedUIDataModelChanges),
          this.#store.select(AiSelectors.selectedAgent).pipe(pairwise()),
          this.#store.select(AiSelectors.sessionId).pipe(pairwise()),
          this.#store.select(AiSelectors.messages).pipe(pairwise())
        ),
        tap(([, dataModelChanges, [selectedAgent], [sessionId], [prevMessages]]) => {
          if (dataModelChanges) {
            return
          }
          this.#analyticsService.track(
            {
              object: AiAnalyticsObject.AI,
              action: AiAnalyticsAction.SESSION_FINISHED,
            } satisfies AiAnalyticsSessionFinishedEvent,
            {
              agent: selectedAgent,
              session_id: sessionId,
              session_length: prevMessages.length ?? 0,
              outcome: 'accepted',
            } satisfies AiAnalyticsSessionFinishedParams
          )

          this.#previousAnalyticsSessionData.set({
            agent: null,
            session_id: null,
            session_length: 0,
          })
        })
      ),
    {
      dispatch: false,
    }
  )

  onInspectorGetWarningsCount = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiInspectorActions.getWarningsCount),
      switchMap(({ application_id, application_version, process_id, category }) =>
        this.#aiInspectorApiService
          .getWarningsCount({
            application_id,
            application_version,
            process_id,
            category,
          })
          .pipe(
            takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
            map((response) =>
              AiInspectorActions.getWarningsCountSuccess({ count: response as number })
            ),
            catchError((error) => of(AiInspectorActions.getWarningsCountFailure({ error })))
          )
      )
    )
  )

  onInspectorGetWarningsMessages = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiInspectorActions.getWarningsMessages),
      switchMap(({ application_id, application_version, process_id, category }) =>
        this.#aiInspectorApiService
          .getWarningsMessages({ application_id, application_version, process_id, category })
          .pipe(
            takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
            map((response) =>
              AiInspectorActions.getWarningsMessagesSuccess({ warnings: response })
            ),
            catchError((error) => of(AiInspectorActions.getWarningsMessagesFailure({ error })))
          )
      )
    )
  )

  onInspectorUpdateWarningStatus = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiInspectorActions.updateWarningStatus),
      switchMap(({ warning_id, status }) =>
        this.#aiInspectorApiService.updateWarningStatus({ warning_id, status }).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map(() => AiInspectorActions.updateWarningStatusSuccess()),
          catchError((error) => of(AiInspectorActions.updateWarningStatusFailure({ error })))
        )
      )
    )
  )
  onInspectorUpdateWarningStatusSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiInspectorActions.updateWarningStatusSuccess),
      withLatestFrom(
        this.#store.select(AiSelectors.getAppId),
        this.#store.select(AiSelectors.getAppVersionId),
        this.#store.select(AiSelectors.getProcessId)
      ),
      map(([, application_id, application_version, process_id]) =>
        AiInspectorActions.getWarningsMessages({
          application_id,
          application_version,
          process_id,
          category: '',
        })
      )
    )
  )

  onInspectorCheckProcess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiInspectorActions.checkProcess),
      map(() => {
        // Use the SSE deep inspection with full process context
        const processContext = this.#aiContextService.getCurrentProcessContext()
        return AiInspectorActions.inspectProcess({ processContext })
      })
    )
  )

  onInspectorGetRules = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiInspectorActions.getRules),
      switchMap(({ category }) =>
        this.#aiInspectorApiService.getRules(category as WarningCategory).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiInspectorActions.getRulesSuccess({ rules: response })),
          catchError((error) => of(AiInspectorActions.getRulesFailure({ error })))
        )
      )
    )
  )

  analyticsSessionRejectedEvents = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiActions.discardDraft),
        withLatestFrom(
          this.#store.select(AiSelectors.selectedAgent),
          this.#store.select(AiSelectors.sessionId),
          this.#store.select(AiSelectors.messages).pipe(pairwise())
        ),
        tap(([, selectedAgent, sessionId, [prevMessages]]) => {
          this.#analyticsService.track(
            {
              object: AiAnalyticsObject.AI,
              action: AiAnalyticsAction.SESSION_FINISHED,
            } satisfies AiAnalyticsSessionFinishedEvent,
            {
              agent: selectedAgent,
              session_id: sessionId,
              session_length: prevMessages.length ?? 0,
              outcome: 'rejected',
            } satisfies AiAnalyticsSessionFinishedParams
          )

          this.#previousAnalyticsSessionData.set({
            agent: null,
            session_id: null,
            session_length: 0,
          })
        })
      ),
    {
      dispatch: false,
    }
  )

  analyticsSessionFeedbackEvents = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiActions.feedbackClick),
        withLatestFrom(
          this.#store.select(AiSelectors.selectedAgent),
          this.#store.select(AiSelectors.sessionId),
          this.#store.select(AiSelectors.messages)
        ),
        tap(([{ feedbackType }, selectedAgent, sessionId, messages]) => {
          this.#analyticsService.track(
            {
              object: AiAnalyticsObject.AI,
              action: AiAnalyticsAction.SESSION_FEEDBACK,
            } satisfies AiAnalyticsSessionFeedbackEvent,
            {
              agent: selectedAgent,
              session_id: sessionId,
              session_length: messages.length,
              feedback: feedbackType === FeedbackType.POSITIVE ? 'happy' : 'unhappy',
            } satisfies AiAnalyticsSessionFeedbackParams
          )
        })
      ),
    { dispatch: false }
  )

  initialMessage = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiActions.initialMessage),
        withLatestFrom(
          this.#store.select(AiSelectors.currentEnumeration),
          this.#store.select(AiSelectors.currentNotification),
          this.#store.select(AiSelectors.currentUI),
          this.#store.select(AiSelectors.selectedDesignerNodeId),
          this.#store.select(AiSelectors.businessRuleContext),
          this.#store.select(AiSelectors.expressionContext),
          this.#store.select(selectDataModelEntitiesArray),
          this.#store.select(selectAllDataModelEntities),
          this.#store.select(AiSelectors.pageLocation),
          this.#store.select(AiSelectors.reusableFunctionContext)
        ),
        map(
          ([
            { action_type, message, selectedAgent },
            currentEnumeration,
            currentNotification,
            currentUI,
            selectedDesignerNodeId,
            businessRuleContext,
            expressionContext,
            selectDataModelEntitiesArray,
            selectDataModelEntities,
            pageLocation,
            reusableFunctionContext,
          ]) => {
            switch (selectedAgent) {
              case AiAgent.ARCHITECT:
                switch (action_type) {
                  case 'enumeration':
                  case 'substitution_tags':
                    return {
                      message,
                      selectedAgent,
                      payload: {
                        action_type,
                        context: currentEnumeration,
                        continue_editing: false,
                      },
                    } satisfies InitialMessageContext
                  case 'document_template':
                  case 'notification_template':
                    return {
                      message,
                      selectedAgent,
                      payload: {
                        action_type,
                        context: currentNotification,
                        continue_editing: false,
                      },
                    } satisfies InitialMessageContext
                  case 'data_model':
                    return {
                      message,
                      selectedAgent,
                      payload: {
                        action_type,
                        context: {
                          current_data_model: selectDataModelEntitiesArray,
                          existent: selectDataModelEntities,
                        },
                        continue_editing: false,
                      },
                    } satisfies InitialMessageContext
                  case 'image':
                    return {
                      message,
                      selectedAgent,
                      payload: {
                        action_type,
                        context: {},
                        continue_editing: false,
                      },
                    } satisfies InitialMessageContext
                  default:
                }
                break
              case AiAgent.DESIGNER:
                return {
                  message,
                  selectedAgent,
                  payload: {
                    action_type:
                      currentUI && currentUI.templateConfig.length > 0
                        ? selectedDesignerNodeId
                          ? 'user_interface_section'
                          : 'user_interface_modification'
                        : 'user_interface',
                    context: currentUI && currentUI.templateConfig.length > 0 ? currentUI : null,
                    continue_editing: false,
                  },
                } satisfies InitialMessageContext
              case AiAgent.DEVELOPER: {
                const isOnReusableFunctionPage = pageLocation?.includes('REUSABLE_FUNCTIONS')
                const initialPayload = isOnReusableFunctionPage
                  ? {
                      action_type: 'reusable_function',
                      context: reusableFunctionContext || {},
                      continue_editing: false,
                    }
                  : businessRuleContext
                    ? {
                        action_type: businessRuleContext.expression
                          ? 'business_rule_modification'
                          : 'business_rule',
                        context: {
                          language: businessRuleContext.language,
                          code: businessRuleContext.expression,
                          datasource: JSON.stringify(selectDataModelEntitiesArray),
                        },
                        continue_editing: false,
                      }
                    : expressionContext
                      ? {
                          action_type,
                          context: { ...expressionContext, ui: currentUI || {} },
                          continue_editing: false,
                        }
                      : { action_type: 'business_rule', context: {}, continue_editing: false }

                return {
                  message,
                  selectedAgent,
                  payload: initialPayload,
                } satisfies InitialMessageContext
              }
              case AiAgent.AUDITOR: {
                const currentProcess = AiAnalystUtils.getInitialProcessContext()
                return {
                  message,
                  selectedAgent,
                  payload: {
                    action_type: currentUI ? 'compliance_check_ui' : 'compliance_check_process',
                    context: currentUI || currentProcess || {},
                    continue_editing: false,
                  },
                } satisfies InitialMessageContext
              }
              case AiAgent.ANALYST: {
                const currentProcess = AiAnalystUtils.getInitialProcessContext()
                return {
                  message,
                  selectedAgent,
                  payload: {
                    action_type: currentProcess
                      ? 'business_process_modification'
                      : 'business_process',
                    context: currentProcess || {},
                    continue_editing: false,
                  },
                } satisfies InitialMessageContext
              }
              case AiAgent.INTEGRATOR: {
                const intPageLocation = this.#store.selectSignal(AiSelectors.pageLocation)()
                const isDetailPage = intPageLocation?.includes('DATA_SOURCE_DETAILS')
                return {
                  message,
                  selectedAgent,
                  payload: {
                    action_type: isDetailPage ? 'datasource_edit' : 'datasource',
                    context: {},
                    continue_editing: false,
                  },
                  _isDetailPage: isDetailPage,
                } as any
              }
            }
          }
        ),
        switchMap((context: any) => {
          switch (context.selectedAgent) {
            case AiAgent.INTEGRATOR: {
              // If on detail page, fetch system context first
              if (context._isDetailPage) {
                const routeParams = this.#store.selectSignal(AiSelectors.pageRouteParams)()
                const resourceId = routeParams?.['resourceId']
                if (resourceId) {
                  return this.#systemApiService.getSystemById(resourceId).pipe(
                    switchMap((systemResponse: any) => {
                      const system = systemResponse?.resourceContent || systemResponse
                      // Store system context in state
                      this.#store.dispatch(
                        AiActions.setCurrentSystemContext({ currentSystem: system })
                      )
                      // Call initial message with system context
                      const payloadWithContext = {
                        ...context.payload,
                        context: {
                          current_system: {
                            name: system?.name,
                            baseUrl: system?.baseUrl,
                            authorization: system?.authorization,
                            endpoints: (system?.endpoints || []).map((ep: any) => ({
                              name: ep.name,
                              httpMethod: ep.httpMethod,
                              url: ep.url,
                              description: ep.description,
                            })),
                          },
                        },
                      }
                      return this.#aiIntegratorApiService.initialMessage(payloadWithContext).pipe(
                        takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
                        map((response) =>
                          AiActions.initialMessageSuccess({
                            response,
                            message: context.message,
                            selectedAgent: context.selectedAgent,
                          })
                        ),
                        catchError((error) => of(AiActions.initialMessageFailure({ error })))
                      )
                    }),
                    catchError((error) => of(AiActions.initialMessageFailure({ error })))
                  )
                }
              }
              return this.#aiIntegratorApiService.initialMessage(context.payload).pipe(
                takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
                map((response) =>
                  AiActions.initialMessageSuccess({
                    response,
                    message: context.message,
                    selectedAgent: context.selectedAgent,
                  })
                ),
                catchError((error) => of(AiActions.initialMessageFailure({ error })))
              )
            }
            case AiAgent.ARCHITECT:
              return this.#aiArchitectApiService.initialMessage(context.payload).pipe(
                takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
                map((response) =>
                  AiActions.initialMessageSuccess({
                    response,
                    message: context.message,
                    selectedAgent: context.selectedAgent,
                  })
                ),
                catchError((error) => of(AiActions.initialMessageFailure({ error })))
              )
            case AiAgent.DESIGNER:
              // Designer initial message is handled by the coordinator's getIntro effect
              // via getSSEActions — no separate call needed here.
              return EMPTY
            case AiAgent.DEVELOPER:
              return this.#aiDeveloperApiService.initialMessage(context.payload).pipe(
                takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
                map((response) =>
                  AiActions.initialMessageSuccess({
                    response,
                    message: context.message,
                    selectedAgent: context.selectedAgent,
                  })
                ),
                catchError((error) => of(AiActions.initialMessageFailure({ error })))
              )
            case AiAgent.ANALYST:
              return this.#aiAnalystApiService.initialMessage(context.payload).pipe(
                takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
                map((response) =>
                  AiActions.initialMessageSuccess({
                    response,
                    message: context.message,
                    selectedAgent: context.selectedAgent,
                  })
                ),
                catchError((error) => of(AiActions.initialMessageFailure({ error })))
              )
            case AiAgent.AUDITOR:
              return this.#aiAuditorApiService.initialMessage(context.payload).pipe(
                takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
                map((response) =>
                  AiActions.initialMessageSuccess({
                    response,
                    message: context.message,
                    selectedAgent: context.selectedAgent,
                  })
                ),
                catchError((error) => of(AiActions.initialMessageFailure({ error })))
              )
            default:
          }
        })
      ),
    { dispatch: true }
  )

  #parseSSEEvents(parts: string[]): any[] {
    const events: any[] = []
    for (const part of parts) {
      for (const line of part.split('\n')) {
        if (line.startsWith('data: ')) {
          try {
            events.push(JSON.parse(line.slice(6)))
          } catch {
            // incomplete or malformed JSON, skip
          }
        }
      }
    }
    return events
  }

  #mapEventFilesToUploadContextFile(event: Event): UploadContextFile[] {
    return Array.from((event.target as HTMLInputElement).files).map((file) => ({
      file,
      name: file.name,
      type: file.type || '.' + file.name.split('.').pop(),
      id: uuid(), // TODO: decide which should be the proper key (this is the displayed name in media library)
    }))
  }

  #areFilesAllowed(files: UploadContextFile[], allowedFileTypes: string[] = []): boolean {
    return _every(
      files,
      ({ name, type }) =>
        allowedFileTypes.includes(type) ||
        allowedFileTypes.some((allowedFileType) => name.endsWith(allowedFileType))
    )
  }

  #areAllFilesOfTypeDocument(files: UploadContextFile[]): boolean {
    return _every(files, ({ type }) => documentFileTypes.includes(type))
  }
  #areAllFilesOfTypeProcess(files: UploadContextFile[]): boolean {
    return _every(files, ({ type }) => processFileTypes.includes(type))
  }
}
